import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MLSCoordination.css';

function MLSCoordination({ listingId, userType }) {
  const [mlsData, setMLSData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    propertyDescription: '',
    specialFeatures: '',
    showingInstructions: '',
    lockboxCode: '',
    agentRemarks: ''
  });

  useEffect(() => {
    fetchMLSData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const fetchMLSData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/mls-coordination/listing/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMLSData(response.data);
      if (response.data?.listing_questions_data) {
        try {
          setFormData(JSON.parse(response.data.listing_questions_data));
        } catch (e) {}
      }
    } catch (error) {
      setMLSData(null);
    } finally {
      setLoading(false);
    }
  };

  const startMLSProcess = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/mls-coordination/create', { listing_id: listingId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMLSData();
    } catch (error) {
      alert('Error starting MLS process');
    }
  };

  const submitListingQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/mls-coordination/${mlsData.id}/listing-questions`, 
        { data: formData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Listing information submitted for attorney review!');
      fetchMLSData();
    } catch (error) {
      alert('Error submitting listing questions');
    }
  };

  const getStatusText = (status) => {
    const texts = {
      'listing_questions': 'Complete Listing Information',
      'attorney_review': 'Pending Attorney Review',
      'attorney_approved': 'Approved - Pending Payment',
      'attorney_rejected': 'Needs Revisions',
      'payment_pending': 'Payment Required',
      'payment_completed': 'Payment Received',
      'agent_assigned': 'Agent Assigned',
      'mls_listed': 'Live on MLS!'
    };
    return texts[status] || status;
  };

  if (loading) return <div className="mls-loading">Loading...</div>;

  // Not started yet - show start button
  if (!mlsData) {
    return (
      <div className="mls-not-started">
        <div className="mls-benefits">
          <div className="benefit-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Maximum exposure to buyers</span>
          </div>
          <div className="benefit-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Flat fee - save thousands</span>
          </div>
          <div className="benefit-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Attorney-reviewed contracts</span>
          </div>
          <div className="benefit-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Licensed agent support</span>
          </div>
        </div>
        <p className="mls-fee-note">MLS listing is included with your $695 flat fee.</p>
        <button className="btn-mls-start" onClick={startMLSProcess}>
          Start MLS Listing Process
        </button>
      </div>
    );
  }

  // Show status-specific content
  return (
    <div className="mls-content">
      {/* Status Badge */}
      <div className={`mls-status-badge status-${mlsData.status}`}>
        {mlsData.status === 'attorney_review' || mlsData.status === 'listing_questions' ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        ) : mlsData.status === 'mls_listed' ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        )}
        <span>{getStatusText(mlsData.status)}</span>
      </div>

      {/* Listing Questions Form */}
      {mlsData.status === 'listing_questions' && (
        <div className="mls-form">
          <p className="form-intro">Provide details that will appear on the MLS listing.</p>

          <div className="form-group">
            <label>Property Description *</label>
            <textarea
              value={formData.propertyDescription}
              onChange={(e) => setFormData({...formData, propertyDescription: e.target.value})}
              rows={4}
              placeholder="Describe your property in detail..."
            />
          </div>

          <div className="form-group">
            <label>Special Features</label>
            <textarea
              value={formData.specialFeatures}
              onChange={(e) => setFormData({...formData, specialFeatures: e.target.value})}
              rows={2}
              placeholder="Pool, updated kitchen, new roof, etc..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Showing Instructions</label>
              <input
                type="text"
                value={formData.showingInstructions}
                onChange={(e) => setFormData({...formData, showingInstructions: e.target.value})}
                placeholder="Call/text listing agent..."
              />
            </div>
            <div className="form-group">
              <label>Lockbox Code</label>
              <input
                type="text"
                value={formData.lockboxCode}
                onChange={(e) => setFormData({...formData, lockboxCode: e.target.value})}
                placeholder="For agent access"
              />
            </div>
          </div>

          <button className="btn-submit" onClick={submitListingQuestions}>
            Submit for Review
          </button>
        </div>
      )}

      {/* Attorney Review */}
      {mlsData.status === 'attorney_review' && (
        <div className="mls-message">
          <p>Our attorney team is reviewing your listing. This usually takes 1-2 business days.</p>
        </div>
      )}

      {/* Payment Required */}
      {(mlsData.status === 'attorney_approved' || mlsData.status === 'payment_pending') && (
        <div className="mls-payment">
          <p>Your contract has been approved! Complete payment to proceed.</p>
          <div className="payment-amount">${mlsData.flat_fee_amount || 499}</div>
          <button className="btn-pay">Pay Now</button>
        </div>
      )}

      {/* Live on MLS */}
      {mlsData.status === 'mls_listed' && (
        <div className="mls-live">
          <div className="mls-live-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h3 className="mls-live-title">Your Listing is Live!</h3>
          <p className="mls-number">MLS #: <strong>{mlsData.mls_listing_number}</strong></p>
          
          <div className="mls-url-box">
            <input 
              type="text" 
              readOnly 
              value={`https://liberastro.com/listings/${listingId}`}
              className="mls-url-input"
            />
            <button 
              className="btn-copy-link"
              onClick={() => {
                navigator.clipboard.writeText(`https://liberastro.com/listings/${listingId}`);
                alert('Link copied to clipboard!');
              }}
            >
              Copy Link
            </button>
          </div>
          
          <a 
            href={`/listings/${listingId}`}
            className="btn-view-listing"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Your Listing
          </a>
        </div>
      )}
    </div>
  );
}

export default MLSCoordination;
